// FILE: ThreadRecapSection.tsx
// Purpose: Settings → Advanced → Recap del hilo: whether the server writes a
//          one-line "where this thread stands" when a turn settles, for the
//          whole installation.
// Layer: Web settings presentation
// Exports: ThreadRecapSection

import { useCallback, useEffect, useState } from "react";

import { SystemSettingsClient } from "~/lib/systemSettingsClient";

import { Switch } from "../ui/switch";

/**
 * No optimistic update, matching `FilePreviewSection` and
 * `GenerationDefaultsSection`: the switch reflects the server's answer rather
 * than an assumed one. That matters more here than elsewhere — the thing being
 * toggled spends money, so the screen must never show "on" for an
 * installation the server still considers off.
 */
export function ThreadRecapSection() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void SystemSettingsClient.readThreadRecap()
      .then((value) => {
        if (!cancelled) setEnabled(value.enabled);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : "No se pudo cargar el ajuste de recap.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback(async (next: boolean) => {
    setIsSaving(true);
    setError(null);
    try {
      const stored = await SystemSettingsClient.writeThreadRecap({ enabled: next });
      setEnabled(stored.enabled);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "No se pudo guardar el cambio.");
    } finally {
      setIsSaving(false);
    }
  }, []);

  if (error !== null && enabled === null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        {error}
      </p>
    );
  }

  if (enabled === null) {
    return (
      <p className="rounded-xl border border-border/70 px-4 py-3 text-xs text-muted-foreground">
        Cargando los ajustes de recap…
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-border/70 px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">Recap del hilo</p>
          <p className="text-xs text-muted-foreground">
            Cuando termina un turno en un hilo que alguien ha dejado de mirar, escribe una línea con
            el estado del hilo y cuál es el siguiente paso, y la muestra al final de la
            conversación. Desactivado de fábrica: cada recap es una llamada al modelo aparte, así
            que el gasto aparece en Usage bajo la operación{" "}
            <code className="mx-0.5">Recap del hilo</code>.
          </p>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Esto sólo abre la puerta. Quién lo recibe se decide persona a persona con el permiso{" "}
            <code className="mx-0.5">threads.recap</code> en Usuarios, y de fábrica no lo tiene
            nadie. Un hilo cuya gente no lo tenga no gasta nada.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={isSaving}
          onCheckedChange={(checked) => {
            void persist(Boolean(checked));
          }}
          aria-label="Recap del hilo"
        />
      </div>

      {error !== null ? <p className="text-xs text-destructive-foreground">{error}</p> : null}
    </div>
  );
}
